<script setup lang="ts">
import { computed, onMounted, ref, type Component } from 'vue';
import { storeToRefs } from 'pinia';
import { useSmarticoMissions } from '@/stores/smartico/missions';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import SwiperHeader from '@/components/perfil-oficial/SwiperHeader.vue';
import MissionDisponiveis from '@/components/smartico/apostatudo/items/mission-cards/MissionDisponiveis.vue';
import MissionParticipando from '@/components/smartico/apostatudo/items/mission-cards/MissionParticipando.vue';
import MissionConcluidos from '@/components/smartico/apostatudo/items/mission-cards/MissionConcluidos.vue';
import MissionExpirados from '@/components/smartico/apostatudo/items/mission-cards/MissionExpirados.vue';
import MissionIndisponiveis from '@/components/smartico/apostatudo/items/mission-cards/MissionIndisponiveis.vue';
import {
  getMissionStatus,
  getMissionStatusRank,
  type MissionStatus,
} from '~/composables/useMissionButton';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';

// Mapeamento de componente por status
const missionComponentMap: Record<MissionStatus, Component> = {
  claimable: MissionConcluidos,
  optIn: MissionDisponiveis,
  participating: MissionParticipando,
  completed: MissionConcluidos,
  expired: MissionExpirados,
  locked: MissionIndisponiveis,
};

// Função para obter o componente correto baseado no status da missão
function getMissionComponent(mission: TMissionOrBadge) {
  const status = getMissionStatus(mission);

  return missionComponentMap[status];
}

type SwiperControls = {
  scrollNext: () => void;
  scrollPrev: () => void;
  currentScrollLeft: Ref<number>;
  currentScrollWidth: Ref<number>;
  currentClientWidth: Ref<number>;
};

const props = defineProps<{
  config: AffiliateConfig;
  iconPath?: string;
  slug?: string;
  isRoletasPage: boolean;
}>();

const { missions, isLoading: isLoadingMissions } = storeToRefs(
  useSmarticoMissions(),
);

const { loadMissions } = useSmarticoMissions();

const afiliadoSwiperRef = ref<
  (InstanceType<typeof PerfilOficialSwiper> & SwiperControls) | null
>(null);

onMounted(async () => {
  await loadMissions();
});

const affiliateMissions = computed(() => {
  let normalizedFilters: string[] = [];

  if (Array.isArray(props.config.smarticoMissionFilter)) {
    normalizedFilters = props.config.smarticoMissionFilter
      .filter((f) => typeof f === 'string')
      .map((f: string) => f.toUpperCase());
  } else if (props.config.smarticoMissionFilter) {
    normalizedFilters = [
      String(props.config.smarticoMissionFilter).toUpperCase(),
    ];
  }

  const ALBUM_CUSTOM_SECTION_ID = 1541;

  const filteredMissions = missions.value.filter((mission: TMissionOrBadge) => {
    // Expired sempre escondidas. Locked só aparecem se forem da seção Figurinhas
    // (ali o card mostra overlay com unlock_mission_description).
    const status = getMissionStatus(mission);

    if (status === 'expired') {
      return false;
    }

    if (
      status === 'locked' &&
      mission.custom_section_id !== ALBUM_CUSTOM_SECTION_ID
    ) {
      return false;
    }

    // If there are no filters or only empty string filters, show all missions.
    if (
      normalizedFilters.length === 0 ||
      normalizedFilters.every((filter) => filter === '')
    ) {
      return true;
    }

    // Get mission custom data as an array of strings
    let missionDataItems: string[] = [];

    if (mission.custom_data) {
      if (Array.isArray(mission.custom_data)) {
        missionDataItems = mission.custom_data.map(String);
      } else {
        missionDataItems = String(mission.custom_data)
          .split(',')
          .map((item) => item.trim());
      }
    }

    // If filters are present but mission custom data is missing or empty, don't show.
    if (missionDataItems.length === 0) {
      return false;
    }

    // Otherwise, apply the filters.
    // Check if any of the missionDataItems (in uppercase) exactly matches any normalizedFilter (in uppercase).
    return normalizedFilters.some((filter) =>
      missionDataItems.some(
        (item) => item.toUpperCase() === filter.toUpperCase(),
      ),
    );
  });

  // Sort missions by status rank first, then by position
  filteredMissions.sort((a: TMissionOrBadge, b: TMissionOrBadge) => {
    const rankDiff = getMissionStatusRank(a) - getMissionStatusRank(b);

    if (rankDiff !== 0) return rankDiff;

    return a.position - b.position;
  });

  return filteredMissions;
});

const dynamicMissionTitle = computed(() => {
  const {
    smarticoMissionTitles,
    smarticoMissionFilter,
    originalSmarticoMissionFilter,
  } = props.config;

  if (
    smarticoMissionTitles &&
    smarticoMissionTitles.length > 0 &&
    smarticoMissionFilter &&
    smarticoMissionFilter.length > 0 &&
    originalSmarticoMissionFilter
  ) {
    const currentFilter = smarticoMissionFilter[0];
    const filterIndex = originalSmarticoMissionFilter.findIndex(
      (filter) => filter === currentFilter,
    );

    if (filterIndex !== -1 && smarticoMissionTitles[filterIndex]) {
      return smarticoMissionTitles[filterIndex];
    }
  }

  return 'Missões';
});
</script>

<template>
  <div
    v-if="!isLoadingMissions && affiliateMissions.length > 0"
    :class="$style.missionsContainer"
  >
    <PerfilOficialSwiper
      ref="afiliadoSwiperRef"
      :icon-path="props.iconPath || 'perfil-oficial/mission'"
      :see-more-url="props.slug ? `${props.slug}` : '/vip/missions'"
      :title="dynamicMissionTitle"
    >
      <template #header-actions>
        <SwiperHeader
          :show-navigation="false"
          :swiper-ref="afiliadoSwiperRef ?? undefined"
        />
      </template>
      <div
        v-for="mission in affiliateMissions"
        :key="mission.id"
        class="flex ml-0.5"
      >
        <component
          :is="getMissionComponent(mission)"
          :is-featured="mission.dt_start !== null"
          :mission="mission"
        />
      </div>
    </PerfilOficialSwiper>
  </div>
</template>

<style module lang="scss">
.missionsContainer {
  @apply mb-4 md:mb-8;
}

.CasinoGamesHeader {
  @apply items-center lg:justify-between pb-1.5 md:pb-2;
}

.header {
  @apply flex flex-col justify-between items-start text-left md:flex-row md:items-center md:mb-3 lg:mb-0;
}

.navigationButton {
  @apply hover:text-primary cursor-pointer text-texts/60;
  user-select: none;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.title-missions {
  user-select: none;
}

.titleText {
  user-select: none;
}

/* Apply to the entire header container to prevent selection on all child elements */
.title-missions > * {
  user-select: none;
}
</style>
